import { PRICE_CACHE_TTL_MS } from './constants';

// Cache client (localStorage) des prix courants, clé par CoinGecko id. Permet d'afficher
// immédiatement le dernier prix connu au montage d'une page (stale-while-revalidate) au lieu
// d'attendre la file CoinGecko (concurrence 1, voir coingecko.ts) pour chaque token, et évite de
// refaire un appel réseau pour un prix déjà récupéré il y a moins de PRICE_CACHE_TTL_MS.

const STORAGE_KEY = 'cryptotrack_current_price_cache';

interface CacheEntry {
  price: number;
  timestamp: number;
}

type CacheMap = Record<string, CacheEntry>;

const readCache = (): CacheMap => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as CacheMap) : {};
  } catch {
    // localStorage indisponible (navigation privée, quota dépassé, SSR) — cache best-effort
    return {};
  }
};

const writeCache = (cache: CacheMap): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cache));
  } catch {
    // Écriture best-effort : une erreur ici ne doit jamais casser l'affichage des prix
  }
};

/** Dernier prix connu pour ce coin, quel que soit son âge (undefined si jamais mis en cache). */
export const getLastKnownPrice = (coinId: string): number | undefined => {
  return readCache()[coinId]?.price;
};

/** Prix en cache s'il est encore dans la fenêtre TTL, sinon `null` (force un refetch réseau). */
export const getFreshCachedPrice = (coinId: string): number | null => {
  const entry = readCache()[coinId];
  if (!entry) return null;
  return Date.now() - entry.timestamp < PRICE_CACHE_TTL_MS ? entry.price : null;
};

export const setCachedPrice = (coinId: string, price: number): void => {
  const cache = readCache();
  cache[coinId] = { price, timestamp: Date.now() };
  writeCache(cache);
};
